import html2canvas from "html2canvas";
import { jsPDF } from "jspdf";
import { formatMYR } from "./api";

/**
 * Generate a Monthly Financial Report PDF containing:
 *   - Store header + month
 *   - Revenue / Cost / Profit / Orders KPIs
 *   - Platform breakdown
 *   - Payouts list (paid vs pending)
 *   - Net after payouts
 */
export async function downloadMonthlyReportPDF({ month, summary, payouts }) {
    let logoDataUrl = "";
    try {
        const res = await fetch("/apple-touch-icon.png", { cache: "force-cache" });
        const blob = await res.blob();
        logoDataUrl = await new Promise((resolve, reject) => {
            const fr = new FileReader();
            fr.onload = () => resolve(fr.result);
            fr.onerror = reject;
            fr.readAsDataURL(blob);
        });
    } catch { /* no logo */ }

    // A4 aspect ratio: 800 x ~1131 px. Force minHeight + flex column so footer sits at bottom.
    const node = document.createElement("div");
    node.style.cssText = `position:fixed;left:-10000px;top:0;width:800px;min-height:1131px;padding:40px 44px;background:white;color:#0F172A;font-family:"Noto Sans SC",-apple-system,sans-serif;display:flex;flex-direction:column;`;

    const totalPayoutsPaid = payouts.filter((p) => p.status === "paid").reduce((n, p) => n + p.amount, 0);
    const totalPayoutsPending = payouts.filter((p) => p.status !== "paid").reduce((n, p) => n + p.amount, 0);
    const netAfter = (summary.total_profit || 0) - totalPayoutsPaid;

    const platformRows = (summary.platform_breakdown || [])
        .map((p) => `<tr><td style="padding:8px 6px;font-size:12px;border-bottom:1px solid #E2E8F0;">${escapeHtml(p.name)}</td><td style="padding:8px 6px;font-size:12px;text-align:right;font-family:'JetBrains Mono',monospace;border-bottom:1px solid #E2E8F0;">${p.count} 单</td><td style="padding:8px 6px;font-size:12px;text-align:right;font-family:'JetBrains Mono',monospace;font-weight:600;border-bottom:1px solid #E2E8F0;">${formatMYR(p.revenue)}</td></tr>`)
        .join("");

    const payoutRows = payouts
        .map((p) => `<tr><td style="padding:8px 6px;font-size:12px;border-bottom:1px solid #E2E8F0;">${escapeHtml(p.recipient)}</td><td style="padding:8px 6px;font-size:12px;text-align:right;font-family:'JetBrains Mono',monospace;border-bottom:1px solid #E2E8F0;">${p.percent || 0}%</td><td style="padding:8px 6px;font-size:12px;text-align:right;font-family:'JetBrains Mono',monospace;font-weight:600;border-bottom:1px solid #E2E8F0;">${formatMYR(p.amount)}</td><td style="padding:8px 6px;font-size:11px;border-bottom:1px solid #E2E8F0;">${p.status === "paid" ? `<span style="color:#B08A3E;">已转账 ${escapeHtml(p.transfer_date || "")}</span>` : `<span style="color:#B45309;">待转账</span>`}</td></tr>`)
        .join("");

    node.innerHTML = `
        <div style="flex:0 0 auto;">
        <div style="display:flex;justify-content:space-between;align-items:flex-start;border-bottom:2px solid #B08A3E;padding-bottom:20px;">
            <div style="display:flex;align-items:center;gap:14px;">
                ${logoDataUrl ? `<img src="${logoDataUrl}" alt="" style="width:64px;height:64px;object-fit:contain;border-radius:8px;" />` : ""}
                <div>
                    <div style="font-size:11px;letter-spacing:2px;text-transform:uppercase;color:#64748B;">FuYuan Jewelry · Monthly Report</div>
                    <div style="font-size:22px;font-weight:800;margin-top:4px;">福缘珠宝 · 月度财务报表</div>
                </div>
            </div>
            <div style="text-align:right;">
                <div style="font-size:11px;letter-spacing:2px;text-transform:uppercase;color:#64748B;">Period</div>
                <div style="font-size:20px;font-weight:700;font-family:'JetBrains Mono',monospace;margin-top:4px;">${escapeHtml(month)}</div>
            </div>
        </div>

        <div style="margin-top:24px;display:grid;grid-template-columns:repeat(4,1fr);gap:12px;">
            <div style="padding:14px;background:#FBF7EF;border-radius:8px;">
                <div style="font-size:10px;text-transform:uppercase;letter-spacing:1px;color:#6E5424;">销售额（毛）</div>
                <div style="font-size:18px;font-weight:800;font-family:'JetBrains Mono',monospace;color:#B08A3E;margin-top:4px;">${formatMYR(summary.total_revenue || 0)}</div>
            </div>
            <div style="padding:14px;background:#FEF3C7;border-radius:8px;">
                <div style="font-size:10px;text-transform:uppercase;letter-spacing:1px;color:#92400E;">销售成本</div>
                <div style="font-size:18px;font-weight:800;font-family:'JetBrains Mono',monospace;color:#B45309;margin-top:4px;">${formatMYR(summary.total_cost || 0)}</div>
            </div>
            <div style="padding:14px;background:#CCFBF1;border-radius:8px;">
                <div style="font-size:10px;text-transform:uppercase;letter-spacing:1px;color:#115E59;">净利润</div>
                <div style="font-size:18px;font-weight:800;font-family:'JetBrains Mono',monospace;color:#0F766E;margin-top:4px;">${formatMYR(summary.total_profit || 0)}</div>
            </div>
            <div style="padding:14px;background:#DBEAFE;border-radius:8px;">
                <div style="font-size:10px;text-transform:uppercase;letter-spacing:1px;color:#1E3A8A;">订单</div>
                <div style="font-size:18px;font-weight:800;font-family:'JetBrains Mono',monospace;color:#2563EB;margin-top:4px;">${summary.total_orders || 0}</div>
            </div>
        </div>

        ${platformRows ? `
        <div style="margin-top:28px;">
            <div style="font-size:12px;letter-spacing:1.5px;text-transform:uppercase;color:#64748B;font-weight:600;margin-bottom:8px;">各平台营收</div>
            <table style="width:100%;border-collapse:collapse;">
                <thead>
                    <tr style="border-bottom:2px solid #0F172A;">
                        <th style="padding:8px 6px;text-align:left;font-size:11px;color:#64748B;text-transform:uppercase;letter-spacing:1px;">平台</th>
                        <th style="padding:8px 6px;text-align:right;font-size:11px;color:#64748B;text-transform:uppercase;letter-spacing:1px;">订单</th>
                        <th style="padding:8px 6px;text-align:right;font-size:11px;color:#64748B;text-transform:uppercase;letter-spacing:1px;">营收</th>
                    </tr>
                </thead>
                <tbody>${platformRows}</tbody>
            </table>
        </div>` : ""}

        ${payoutRows ? `
        <div style="margin-top:28px;">
            <div style="font-size:12px;letter-spacing:1.5px;text-transform:uppercase;color:#64748B;font-weight:600;margin-bottom:8px;">分成记录</div>
            <table style="width:100%;border-collapse:collapse;">
                <thead>
                    <tr style="border-bottom:2px solid #0F172A;">
                        <th style="padding:8px 6px;text-align:left;font-size:11px;color:#64748B;text-transform:uppercase;letter-spacing:1px;">对象</th>
                        <th style="padding:8px 6px;text-align:right;font-size:11px;color:#64748B;text-transform:uppercase;letter-spacing:1px;">%</th>
                        <th style="padding:8px 6px;text-align:right;font-size:11px;color:#64748B;text-transform:uppercase;letter-spacing:1px;">金额</th>
                        <th style="padding:8px 6px;text-align:left;font-size:11px;color:#64748B;text-transform:uppercase;letter-spacing:1px;">状态</th>
                    </tr>
                </thead>
                <tbody>${payoutRows}</tbody>
            </table>
            <div style="margin-top:12px;display:flex;justify-content:space-between;font-size:12px;">
                <span style="color:#64748B;">已转账 <span style="font-family:'JetBrains Mono',monospace;color:#B08A3E;font-weight:600;">${formatMYR(totalPayoutsPaid)}</span></span>
                <span style="color:#64748B;">待转账 <span style="font-family:'JetBrains Mono',monospace;color:#B45309;font-weight:600;">${formatMYR(totalPayoutsPending)}</span></span>
            </div>
        </div>` : ""}

        <div style="margin-top:32px;padding:16px;background:#F8FAFC;border-radius:8px;border-left:4px solid ${netAfter >= 0 ? "#B08A3E" : "#DC2626"};">
            <div style="display:flex;justify-content:space-between;align-items:baseline;">
                <div>
                    <div style="font-size:11px;letter-spacing:1.5px;text-transform:uppercase;color:#64748B;font-weight:600;">最终净收入 Net Income</div>
                    <div style="font-size:11px;color:#94A3B8;margin-top:2px;">净利润 − 已转账分成</div>
                </div>
                <div style="font-size:26px;font-weight:800;font-family:'JetBrains Mono',monospace;color:${netAfter >= 0 ? "#B08A3E" : "#DC2626"};">${formatMYR(netAfter)}</div>
            </div>
        </div>
        </div>

        <div style="flex:1 1 auto;"></div>

        <div style="flex:0 0 auto;margin-top:28px;padding-top:16px;border-top:2px solid #B08A3E;display:flex;justify-content:space-between;align-items:center;font-size:11px;color:#94A3B8;">
            <span style="color:#0F172A;font-weight:600;">FuYuan Jewelry · 福缘珠宝 · 月度报表</span>
            <span style="font-family:'JetBrains Mono',monospace;">Generated ${new Date().toLocaleString("zh-CN")}</span>
        </div>
    `;

    document.body.appendChild(node);
    try {
        await document.fonts?.ready?.catch(() => {});
        const canvas = await html2canvas(node, { scale: 2, backgroundColor: "#ffffff", useCORS: true });
        const imgData = canvas.toDataURL("image/jpeg", 0.92);

        const pdf = new jsPDF({ unit: "mm", format: "a4", orientation: "portrait" });
        const pageW = pdf.internal.pageSize.getWidth();
        const pageH = pdf.internal.pageSize.getHeight();
        const imgW = pageW - 20;
        const imgH = (canvas.height * imgW) / canvas.width;

        if (imgH <= pageH - 20) {
            pdf.addImage(imgData, "JPEG", 10, 10, imgW, imgH);
        } else {
            const ratio = imgW / canvas.width;
            const pageCanvasH = (pageH - 20) / ratio;
            let y = 0;
            while (y < canvas.height) {
                const sliceCanvas = document.createElement("canvas");
                sliceCanvas.width = canvas.width;
                sliceCanvas.height = Math.min(pageCanvasH, canvas.height - y);
                sliceCanvas.getContext("2d").drawImage(canvas, 0, -y);
                const sliceData = sliceCanvas.toDataURL("image/jpeg", 0.92);
                if (y > 0) pdf.addPage();
                pdf.addImage(sliceData, "JPEG", 10, 10, imgW, sliceCanvas.height * ratio);
                y += pageCanvasH;
            }
        }
        pdf.save(`Report-${month}.pdf`);
    } finally {
        node.remove();
    }
}

function escapeHtml(s) {
    return String(s ?? "")
        .replace(/&/g, "&amp;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;")
        .replace(/"/g, "&quot;");
}
